import { createStdRequest, apiFetch, logWithTime } from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

class SocketAccess {
  socket = null;
  refreshCallbacks = [];

  setUpWebSocket() {
    const location = window.location;
    const webSocketProtocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const webSocketURI = webSocketProtocol + '//' + location.host + '/ws';

    this.socket = new WebSocket(webSocketURI);

    this.socket.onopen = () => {
      this.log('Connected.');
      this.setWebSocketFirmId();
    };

    this.socket.onclose = (event) => {
      if (event.wasClean) {
        this.log('Disconnected.');
      } else {
        this.log('Connection lost.'); // for example if server processes is killed
      }
      this.log('Code: ' + event.code + '. Reason: ' + event.reason);
    };

    this.socket.onmessage = (event) => {
      this.handleReceivedData(event.data);
    };

    this.socket.onerror = (error) => {
      this.log('Error: ' + error.message);
    };
  }

  handleReceivedData(dataAsString) {
    this.log(`From Socket: ${dataAsString}`);
    const data = JSON.parse(dataAsString);
    const { type } = data;
    if (type === 'reduxUpdate') {
      this.handleReduxUpdate(data);
    }
  }

  handleReduxUpdate(data) {
    const kind = data.kind;
    this.log(`kind: ${kind}`);
    let personId = 0;
    let continuation;
    let uri;
    let request;
    let actionOnGoodResponse;
    const operation = data.operation;
    let onType;
    let augmentedKind;
    let role;
    let oldOwnerId;
    let newOwnerId;
    this.log(`operation: ${operation}`);
    switch (kind) {
      case 'person':
        personId = data.personId;
        this.log(`personId: ${personId}`);
        if (operation === 'delete') {
          role = data.role;
          if (role === 'client') {
            ReduxLoader.removePersonAsClient(personId);
          } else if (role === 'emergency contact') {
            const clientId = data.clientId;
            ReduxLoader.removePersonAsEmergencyContact(clientId, personId);
          }
        } else if (operation === 'add' || operation === 'modify') {
          role = data.role;
          if (role == 'client') {
            ReduxLoader.loadOrRefreshClient(personId, () => this.refreshPage('person'));
          } else {
            ReduxLoader.loadOrRefreshPerson(personId, () => this.refreshPage('person'));
          }
        }
        break;

      case 'client':
        if (operation === 'bulkOwnershipChange') {
          oldOwnerId = data.oldOwnerId;
          newOwnerId = data.newOwnerId;
          ReduxLoader.changeClientOwnerships(oldOwnerId, newOwnerId);
        }
        break;

      case 'referralContact':
        if (operation === 'bulkOwnershipChange') {
          oldOwnerId = data.oldOwnerId;
          newOwnerId = data.newOwnerId;
          ReduxLoader.changeReferralContactOwnerships(oldOwnerId, newOwnerId);
        }
        break;

      case 'task':
        const taskId = data.taskId;
        logWithTime(`SocketAccess sees reduxUpdate operation '${operation}' for task ${taskId}`);
        if (operation === 'add') {
          ReduxLoader.loadTask(taskId, () => this.refreshPage('task'));
        } else if (operation === 'modify') {
          ReduxLoader.refreshTask(taskId, () => this.refreshPage('task'));
        } else if (operation === 'delete') {
          ReduxLoader.deleteTaskWithId(taskId);
          this.refreshPage('task');
        }
        break;

      case 'statusTransition':
        personId = data.personId;
        const transitionId = data.transitionId;
        this.log(`personId: ${personId}; transitionId: ${transitionId}`);

        // get hold of the StatusTransition object, and add it to Redux
        // refresh the Redux version of the person (thus getting the new status)
        // then call the current page's refresh code, if any
        continuation = () => {
          ReduxLoader.addClientToClientsInfo(personId);
          this.refreshPage('statusTransition');
        };
        uri = `/api/status/${transitionId}`;
        request = createStdRequest(uri);
        actionOnGoodResponse = (transition) => {
          const reduxArray = ReduxLoader.convertStatusHistoryArrayApiStructIntoReduxStruct([transition]);
          ReduxLoader.addStatusTransitions(reduxArray);
          console.log("We're back from ReduxLoader.addStatusTransitions()");
          ReduxLoader.loadOrRefreshPerson(personId, continuation);
        };
        apiFetch(null, request, actionOnGoodResponse);
        break;

      case 'outreach':
        if (operation !== 'delete') {
          const outreachId = data.outreachId;
          const outreachDirection = data.direction;
          logWithTime(`SocketAccess sees reduxUpdate for ${outreachDirection} outreach ${outreachId}`);
          // this.log(`outreachId: ${outreachId}; direction: ${outreachDirection}`);
          continuation = () => this.refreshPage(kind);
          if (outreachDirection === 'incoming') {
            ReduxLoader.loadOrRefreshIncomingOutreach(outreachId, continuation);
          } else {
            // TODO this isn't actually implemented yet!
            ReduxLoader.loadOrRefreshOutgoingOutreach(outreachId, continuation);
          }
        } else {
          // ReduxLoader.deleteOutreachWithId(data.outreachId);
          this.refreshPage(kind);
        }
        break;

      case 'missive':
        if (operation !== 'delete') {
          const missiveId = data.missiveId;
          logWithTime(`SocketAccess sees reduxUpdate for missive ${missiveId}`);
          // this.log(`missiveId: ${missiveId}`);
          continuation = () => this.refreshPage('missive');
          ReduxLoader.loadOrRefreshMissive(missiveId, continuation);
        } else {
          ReduxLoader.deleteMissiveWithId(data.missiveId);
          this.refreshPage('missive');
        }
        break;

      case 'callRailCall':
        const callId = data.callId;
        if (operation === 'add') {
          logWithTime(`SocketAccess sees reduxUpdate for callRailCall ${callId}`);
          ReduxLoader.loadOrRefreshCallRailCall(callId, () => this.refreshPage('callRailCall'));
        } else if (operation === 'delete') {
          ReduxLoader.deleteCallRailCall(callId);
          this.refreshPage('callRailCall');
        }
        break;

      case 'inquiry':
        // operation is one of 'add' | 'update' | 'delete'
        if (operation === 'add' || operation === 'update') {
          uri = `/api/inquiry/${data.inquiryId}`;
          request = createStdRequest(uri);
          actionOnGoodResponse = (inquiry) => {
            ReduxLoader.updateInquiry(inquiry);
            this.refreshPage(kind);
          };
          apiFetch(null, request, actionOnGoodResponse);
        } else if (operation === 'delete') {
          // no need for API call, as Inquiry is already deleted from DB
          console.log(`Calling ReduxLoader.deleteInquiryWithId(${data.inquiryId})`);
          ReduxLoader.deleteInquiryWithId(data.inquiryId);
          this.refreshPage(kind);
        } else if (operation === 'bulkOwnershipChange') {
          oldOwnerId = data.oldOwnerId;
          newOwnerId = data.newOwnerId;
          ReduxLoader.changeInquiryOwnerships(oldOwnerId, newOwnerId);
        }
        break;

      case 'note':
        // operation is one of 'add' | 'update' | 'delete'
        onType = data.onType; // // onType is something like 'onPerson' or 'onInquiry'
        const { noteId } = data;
        augmentedKind = kind + ':' + onType;
        if (operation === 'add' || operation === 'update') {
          uri = '/api/note/' + noteId;
          request = createStdRequest(uri);
          actionOnGoodResponse = (note) => {
            if (onType === 'onInquiry') {
              ReduxLoader.addOrUpdateNotesOnInquiries([note]);
            }
            this.refreshPage(augmentedKind);
          };
          apiFetch(null, request, actionOnGoodResponse);
        } /* operation === 'delete' */ else {
          if (onType === 'onInquiry') {
            ReduxLoader.removeNoteOnInquiry(noteId);
          }
          this.refreshPage(augmentedKind);
        }
        break;

      case 'document':
        // operation is one of 'add' | 'update' | 'delete'
        onType = data.onType; // // onType is something like 'onPerson' or 'onInquiry'
        const { documentId } = data;
        augmentedKind = kind + ':' + onType;
        if (operation === 'add') {
          uri = `/api/document/${documentId}`;
          request = createStdRequest(uri);
          actionOnGoodResponse = (document) => {
            ReduxLoader.addDocumentOnInquiry(document);
            this.refreshPage(augmentedKind);
          };
          apiFetch(null, request, actionOnGoodResponse);
        }
        if (operation === 'update') {
          uri = `/api/document/${documentId}`;
          request = createStdRequest(uri);
          actionOnGoodResponse = (document) => {
            ReduxLoader.updateDocumentOnInquiry(document);
            this.refreshPage(augmentedKind);
          };
          apiFetch(null, request, actionOnGoodResponse);
        }
        if (operation === 'delete') {
          if (onType === 'onInquiry') {
            ReduxLoader.removeDocumentOnInquiry(documentId);
          }
          this.refreshPage(augmentedKind);
        }
        break;

      case 'case':
        if (operation === 'delete') {
          ReduxLoader.deleteCaseWithId(data.caseId);
          this.refreshPage(kind);
        }
        break;

      case 'referral':
        if (operation === 'modify') {
          ReduxLoader.refreshReferral(data.referralId, () => this.refreshPage('referral'));
        } else if (operation === 'delete') {
          ReduxLoader.deleteReferralWithId(data.referralId);
          this.refreshPage(kind);
        }
        break;

      default:
        console.log(`In handleReduxUpdate(); Unknown kind: ${kind}`);
        break;
    }
  }

  refreshPage(kind) {
    const listForThisKind = this.refreshCallbacks.filter((entry) => entry.kind === kind);
    this.log(`in refreshPage(${kind}). # of callbacks: ${listForThisKind.length}`);
    listForThisKind.forEach((struct) => struct.refreshFunc());
  }

  registerRefreshCallback(callbackStruct) {
    const { kind, pageName } = callbackStruct;
    const filteredList = this.refreshCallbacks.filter((entry) => !(entry.kind === kind && entry.pageName === pageName));
    const newList = [...filteredList, callbackStruct];
    this.refreshCallbacks = newList;
    this.log(`after adding callback, # of refreshCallbacks: ${this.refreshCallbacks.length}`);
  }

  unregisterRefreshCallbacksForPage(pageName) {
    this.refreshCallbacks = this.refreshCallbacks.filter((struct) => struct.pageName !== pageName);
    this.log(`after removing callbacks, # of refreshCallbacks: ${this.refreshCallbacks.length}`);
  }

  sendToSocket(msg, shouldBroadcast) {
    const xmission = {
      type: shouldBroadcast ? 'broadcast' : 'echo',
      message: msg,
    };
    const jsonString = JSON.stringify(xmission);
    this.socket.send(jsonString);
  }

  sendReduxUpdateToSocket(obj) {
    const typedObj = { type: 'reduxUpdate', ...obj };
    this.sendObjectToSocket(typedObj);
  }

  setWebSocketFirmId() {
    const firmId = DA.getFirmId() || 0;
    const obj = {
      type: 'registerFirmId',
      firmId: firmId,
    };
    this.sendObjectToSocket(obj);
  }

  sendObjectToSocket(obj) {
    const jsonString = JSON.stringify(obj);
    this.log(`To Socket: ${jsonString}`);
    this.socket.send(jsonString);
  }

  sendBroadcast() {
    const random = Math.random();
    const randomInt = Math.floor(random * 10000);
    this.sendToSocket('Broadcast Message ' + randomInt, true);
  }

  log(msg) {
    // console.log(msg);
    // addToOutputArea(msg);
  }

  /*
  addToOutputArea(msg) {
    const area = document.getElementById('outputArea');
    const prevValue = area.value;
    const newValue = prevValue + '\n' + msg;
    area.value = newValue;
  }
  */
}

const inst = new SocketAccess();

export default inst;
